import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, HardHat, Receipt, Wallet, Users, LogOut, Menu, X, BarChart3,
  Building2, Hammer, Warehouse as WarehouseIcon, TrendingUp, KeyRound, Landmark,
  ShieldCheck, ScrollText, FileSignature, Briefcase, FileWarning, CloudDownload,
  ChevronDown, Truck, Wrench, Banknote, Camera, HandCoins, Image as ImageIcon,
  Gauge, FileText, ShoppingCart, Cog,
} from "lucide-react";
import { ROLES } from "@/lib/api";
import IncontiLogo from "@/components/IncontiLogo";
import NotificationBell from "@/components/NotificationBell";
import OfflineIndicator from "@/components/OfflineIndicator";
import PushToggle from "@/components/PushToggle";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import TwoFactorModal from "@/components/TwoFactorModal";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: null },
  {
    label: "Dirección", icon: Gauge, roles: ["admin", "contador"],
    children: [
      { to: "/direction", label: "Panel Directivo", icon: Gauge, roles: ["admin", "contador", "auxiliar", "residente", "supervisor"] },
      { to: "/reports/global", label: "Reportes", icon: BarChart3, roles: ["admin", "contador"] },
    ],
  },
  {
    label: "Área Técnica", icon: HardHat, roles: null,
    children: [
      { to: "/projects", label: "Proyectos / Obras", icon: HardHat, roles: null },
      { to: "/control-obra", label: "Control de Obra", icon: HardHat, roles: ["admin", "contador", "auxiliar", "residente", "supervisor"] },
      { to: "/contracts", label: "Contratos", icon: FileText, roles: ["admin", "contador", "auxiliar"] },
    ],
  },
  {
    label: "Finanzas", icon: Receipt, roles: null,
    children: [
      { to: "/expenses", label: "Gastos", icon: Receipt, roles: null },
      { to: "/cash", label: "Caja / Efectivo", icon: Banknote, roles: null },
      { to: "/treasury-banks", label: "Tesorería y Bancos", icon: Landmark, roles: ["admin", "contador", "auxiliar"] },
      { to: "/staff-contributions", label: "Aportaciones Personal", icon: HandCoins, roles: ["admin", "contador", "auxiliar"] },
      { to: "/loans", label: "Préstamos", icon: Landmark, roles: ["admin", "contador"] },
    ],
  },
  {
    label: "Facturación", icon: FileSignature, roles: ["admin", "contador", "auxiliar"],
    children: [
      { to: "/invoicing", label: "Facturación CFDI", icon: FileSignature, roles: ["admin", "contador", "auxiliar"] },
      { to: "/billing/pending-invoices", label: "Facturas Pendientes", icon: FileWarning, roles: ["admin", "contador", "auxiliar"] },
      { to: "/sat", label: "Facturas SAT", icon: CloudDownload, roles: ["admin", "contador"] },
    ],
  },
  {
    label: "Compras y Almacén", icon: ShoppingCart, roles: null,
    children: [
      { to: "/purchasing", label: "Requisiciones y OC", icon: ShoppingCart, roles: ["admin", "contador", "auxiliar", "residente", "supervisor"] },
      { to: "/warehouses", label: "Almacenes e Inventario", icon: WarehouseIcon, roles: ["admin", "contador", "auxiliar", "residente", "supervisor"] },
      { to: "/vendors", label: "Proveedores", icon: Building2, roles: ["admin", "contador", "auxiliar"] },
    ],
  },
  {
    label: "Recursos Humanos", icon: Users, roles: null,
    children: [
      { to: "/payroll", label: "Nómina", icon: Wallet, roles: ["admin", "contador", "auxiliar", "residente", "supervisor"] },
      { to: "/pieceworkers", label: "Destajistas", icon: Hammer, roles: ["admin", "contador", "auxiliar", "residente"] },
      { to: "/users", label: "Personal / Usuarios", icon: Users, roles: ["admin"] },
    ],
  },
  {
    label: "Flotilla y Taller", icon: Truck, roles: null,
    children: [
      { to: "/fleet", label: "Vehículos", icon: Truck, roles: ["admin", "contador", "auxiliar", "residente", "supervisor"] },
      { to: "/machinery", label: "Maquinaria", icon: Cog, roles: ["admin", "contador", "auxiliar", "residente", "supervisor"] },
      { to: "/workshop", label: "Taller", icon: Wrench, roles: ["admin", "contador", "auxiliar", "mecanico"] },
    ],
  },
  {
    label: "Comercial", icon: Briefcase, roles: ["admin", "contador", "auxiliar"],
    children: [
      { to: "/contracting-entities", label: "Clientes", icon: Briefcase, roles: ["admin", "contador", "auxiliar"] },
      { to: "/investors", label: "Inversionistas", icon: TrendingUp, roles: ["admin", "contador"] },
    ],
  },
  {
    label: "Administración", icon: ScrollText, roles: ["admin", "contador", "auxiliar"],
    children: [
      { to: "/legal", label: "Gestión Documental", icon: FileText, roles: ["admin", "contador", "auxiliar"] },
      { to: "/files-gallery", label: "Galería de archivos", icon: ImageIcon, roles: ["admin", "contador"] },
      { to: "/audit-logs", label: "Auditoría", icon: ScrollText, roles: ["admin", "contador"] },
    ],
  },
  { to: "/companies", label: "Empresas", icon: Building2, roles: null, superadminOnly: true },
];

const NavItem = ({ item, sub = false }) => {
  const { to, label, icon: Icon } = item;
  return (
    <NavLink
      to={to}
      data-testid={`nav-${to.replace(/\//g, "-").replace(/^-/, "")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 mb-1 text-sm font-medium tracking-wide transition-all rounded-sm relative ${
          sub ? "ml-4" : ""
        } ${isActive ? "text-cyan-300" : "text-slate-400 hover:text-white"}`
      }
      style={({ isActive }) =>
        isActive
          ? {
              background: "linear-gradient(90deg, rgba(99,102,241,0.18), rgba(34,211,238,0.04))",
              borderLeft: "2px solid var(--cyan)",
              boxShadow: "inset 0 0 18px rgba(99,102,241,0.12)",
            }
          : { borderLeft: "2px solid transparent" }
      }
    >
      <Icon className="w-[18px] h-[18px]" />
      {label}
    </NavLink>
  );
};

const NavGroup = ({ item, allowed, pathname }) => {
  const children = item.children.filter(allowed);
  const childActive = children.some((c) => pathname.startsWith(c.to));
  const [open, setOpen] = useState(childActive);
  useEffect(() => { if (childActive) setOpen(true); }, [childActive]);
  if (children.length === 0) return null;
  const Icon = item.icon;
  return (
    <div className="mb-1">
      <button
        type="button"
        data-testid={`nav-group-${item.label.toLowerCase()}`}
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium tracking-wide transition-all rounded-sm ${
          childActive ? "text-cyan-300" : "text-slate-400 hover:text-white"
        }`}
        style={{ borderLeft: "2px solid transparent" }}
      >
        <Icon className="w-[18px] h-[18px]" />
        <span className="flex-1 text-left">{item.label}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && children.map((c) => <NavItem key={c.to} item={c} sub />)}
    </div>
  );
};

export default function Layout() {
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [force2FASetup, setForce2FASetup] = useState(false);
  const roleLabel = ROLES.find((r) => r.value === user?.role)?.label || user?.role;

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Si el usuario es admin y NO tiene 2FA activado, abrir modal forzado
  useEffect(() => {
    if (user && user.role === "admin" && user.requires_2fa_setup && !show2FA) {
      setForce2FASetup(true);
      setShow2FA(true);
    }
  }, [user?.id, user?.requires_2fa_setup]); // eslint-disable-line

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const allowed = (item) => {
    if (item.superadminOnly) return !!user?.superadmin;
    if (user?.role === "mecanico") return item.to === "/workshop" || !!item.children?.some((c) => c.to === "/workshop");
    return !item.roles || user?.role === "admin" || item.roles.includes(user?.role);
  };

  return (
    <div className="min-h-screen flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          data-testid="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-40 lg:hidden"
          style={{ background: "rgba(5,8,18,0.7)", backdropFilter: "blur(4px)" }}
        />
      )}

      <aside
        data-testid="sidebar"
        className={`w-64 flex flex-col fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50 transform transition-transform lg:transform-none ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        style={{
          background: "#06090f",
          borderRight: "1px solid var(--border)",
          height: "100dvh",
          maxHeight: "100dvh",
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <div className="px-5 py-5 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          {company && !company.is_default ? (
            <div className="flex items-center gap-2.5 min-w-0" data-testid="company-brand">
              {company.logo_b64 ? (
                <img src={company.logo_b64} alt={company.name} className="w-9 h-9 object-contain rounded-sm" style={{ background: "rgba(255,255,255,0.04)" }} />
              ) : (
                <div className="w-9 h-9 flex items-center justify-center rounded-sm shrink-0"
                  style={{ background: "rgba(34,211,238,0.08)", border: "1px solid rgba(34,211,238,0.25)" }}>
                  <Building2 className="w-4.5 h-4.5 text-cyan-300" style={{ width: 18, height: 18 }} />
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-bold text-slate-100 truncate uppercase font-display">{company.name}</div>
                <div className="text-[9px] text-slate-500 label-mono truncate">// SISTEMA DE GESTIÓN</div>
              </div>
            </div>
          ) : (
            <IncontiLogo size={36} />
          )}
          <button
            data-testid="sidebar-close-btn"
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-slate-400 hover:text-cyan-300 p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 py-5 px-3 overflow-y-auto">
          <div className="label-mono px-3 py-2 mb-1">Navegación</div>
          {NAV.filter(allowed).map((item) =>
            item.children ? (
              <NavGroup key={item.label} item={item} allowed={allowed} pathname={location.pathname} />
            ) : (
              <NavItem key={item.to} item={item} />
            )
          )}
        </nav>

        <div className="border-t p-4" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 flex items-center justify-center font-bold text-sm rounded-sm font-display"
              style={{ background: "rgba(99,102,241,0.12)", color: "#A5B4FC", border: "1px solid rgba(99,102,241,0.35)" }}>
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate text-slate-100">{user?.name}</div>
              <div className="text-[11px] text-slate-500 truncate label-mono">
                {user?.superadmin ? "SUPERADMIN" : roleLabel}
                {user?.superadmin && company && !company.is_default ? ` · ${company.name}` : ""}
              </div>
            </div>
          </div>
          <button
            data-testid="change-password-btn"
            onClick={() => setShowChangePassword(true)}
            className="w-full btn-ghost flex items-center justify-center gap-2 text-xs font-semibold py-2 rounded-sm mb-2"
          >
            <KeyRound className="w-4 h-4" /> CAMBIAR CONTRASEÑA
          </button>
          <button
            data-testid="twofa-btn"
            onClick={() => { setForce2FASetup(false); setShow2FA(true); }}
            className="w-full btn-ghost flex items-center justify-center gap-2 text-xs font-semibold py-2 rounded-sm mb-2"
            style={{ borderColor: "rgba(34,211,238,0.4)", color: "var(--cyan)" }}
          >
            <ShieldCheck className="w-4 h-4" /> 2FA · 2 PASOS
          </button>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full btn-ghost flex items-center justify-center gap-2 text-xs font-semibold py-2 rounded-sm"
          >
            <LogOut className="w-4 h-4" /> CERRAR SESIÓN
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden flex flex-col w-full lg:w-auto">
        {/* Top bar with hamburger (mobile) + notification bell */}
        <div
          className="px-4 lg:px-8 flex items-center justify-between gap-3 border-b sticky top-0 z-30"
          style={{
            borderColor: "var(--border)",
            background: "rgba(5,8,18,0.7)",
            backdropFilter: "blur(8px)",
            paddingTop: "max(0.75rem, env(safe-area-inset-top))",
            paddingBottom: "0.75rem",
          }}
        >
          <button
            data-testid="sidebar-open-btn"
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden btn-ghost p-2 rounded-sm"
            aria-label="Abrir menú"
            style={{ minWidth: 44, minHeight: 44 }}
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="lg:hidden">
            <IncontiLogo size={28} compact />
          </div>
          <div className="flex items-center gap-2 lg:gap-3 ml-auto">
            <OfflineIndicator />
            <PushToggle />
            <NotificationBell />
          </div>
        </div>
        <div className="flex-1">
          <Outlet />
        </div>
      </main>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
      {show2FA && (
        <TwoFactorModal
          forceSetup={force2FASetup}
          onClose={() => { setShow2FA(false); setForce2FASetup(false); }}
        />
      )}
    </div>
  );
}
